import { useMemo, useState, type ComponentType, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { SectionHeader, StatStrip } from "@/components/dashboard/primitives";
import { AlertTriangle, ArrowRight, Calendar, Clock, List, Package, Phone, Shield, Users, Wrench, XCircle, Zap } from "lucide-react";
import {
  useCommandDashboardOperationsData,
  useCommandDashboardStrategicData,
} from "@/hooks/useCommandDashboardServerData";
import type {
  CommandDashboardActionRow,
  CommandDashboardChannelRow,
  CommandDashboardOperationsRow,
  CommandDashboardPeriod,
  CommandDashboardRepRow,
  CommandDashboardStrategicSummary,
} from "@/services/command-dashboard-page.service";

type Lens = "strategic" | "operations";

type Recommendation = {
  key: string;
  subject: string;
  action: string;
  outcome: string;
  severity: "positive" | "neutral" | "caution";
  route: string;
};

const fmtMoney = (value: number) => {
  const safe = Number.isFinite(value) ? value : 0;
  if (Math.abs(safe) >= 1_000_000) return `$${(safe / 1_000_000).toFixed(1)}M`;
  if (Math.abs(safe) >= 1_000) return `$${Math.round(safe / 1_000)}K`;
  return `$${Math.round(safe)}`;
};

const fmtCount = (value: number) => Math.round(Number.isFinite(value) ? value : 0).toLocaleString();

function periodLabel(period: CommandDashboardPeriod): string {
  if (period === "today") return "Today";
  if (period === "week") return "Last 7 days";
  return "Last 30 days";
}

export function CommandDashboardServerLensBody({ lens }: { lens: Lens }) {
  if (lens === "operations") return <CommandDashboardOperationsServer />;
  return <CommandDashboardStrategicServer />;
}

function CommandDashboardStrategicServer() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<CommandDashboardPeriod>("month");
  const { summary, actions, channels, reps, loading, error, refresh } = useCommandDashboardStrategicData({
    enabled: true,
    period,
  });

  const recommendations = useMemo(
    () => buildRecommendations(summary, channels, reps),
    [summary, channels, reps],
  );

  return (
    <div className="space-y-10 max-w-[1600px] mx-auto w-full">
      <header className="flex items-end justify-between gap-6">
        <p className="text-[13px] text-muted-foreground tabular-nums">
          {periodLabel(period)} - <span className="text-foreground font-medium">{fmtMoney(summary.bookedRevenue)}</span> Booked
          {summary.pendingRevenue > 0 && (
            <>
              {" - "}
              <button
                type="button"
                onClick={() => navigate("/intelligence")}
                className="text-[hsl(var(--warning))] font-medium hover:underline underline-offset-4"
              >
                {fmtMoney(summary.pendingRevenue)}
              </button>{" "}
              at Risk
            </>
          )}
          {" - "}<span className="text-foreground font-medium">{fmtCount(summary.installsScheduled)}</span> Install{summary.installsScheduled === 1 ? "" : "s"} Scheduled
        </p>
        <div className="flex items-center gap-3">
          <PeriodSwitch value={period} onChange={setPeriod} />
          <button
            type="button"
            onClick={refresh}
            className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
          >
            Refresh
          </button>
        </div>
      </header>

      {error ? <ServerErrorNotice /> : null}
      {loading ? <LoadingLine /> : null}

      <StatStrip
        items={[
          {
            label: "Net Conversion",
            value: `${summary.totalLeads > 0 ? Math.round((summary.netJobs / summary.totalLeads) * 100) : 0}%`,
            sub: `${fmtCount(summary.netJobs)}/${fmtCount(summary.totalLeads)}`,
          },
          { label: "Net Revenue", value: fmtMoney(summary.netRevenue), sub: `${fmtCount(summary.netJobs)} Net` },
          {
            label: "Pending Revenue",
            value: fmtMoney(summary.pendingRevenue),
            sub: `${fmtCount(Math.max(summary.wonCount - summary.netJobs, 0))} Pending`,
            accent: summary.pendingRevenue > 0 ? "warn" : undefined,
          },
          { label: "Avg CPA", value: summary.avgCpa > 0 ? fmtMoney(summary.avgCpa) : "-", sub: `${channels.length} Active` },
          {
            label: "Clustering",
            value: `${Math.round(summary.clusteringPct)}%`,
            sub: "Scheduled days",
            accent: summary.clusteringPct >= 50 ? "good" : summary.clusteringPct >= 25 ? "warn" : "bad",
          },
        ]}
      />

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div>
          <SectionHeader title="Live Actions" eyebrow={`${summary.missingResults + summary.overdueFollowups + summary.reschedulesNeeded} Live Actions`} />
          <div className="rounded-[10px] border border-border bg-card overflow-hidden">
            {actions.length === 0 ? (
              <EmptyPanel title="You're caught up." body="No command actions need attention for this period." />
            ) : (
              <ul className="divide-y divide-border">
                {actions.map((action, index) => (
                  <ActionRow key={action.actionId} action={action} primary={index === actions.findIndex((a) => a.itemCount > 0)} />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <SectionHeader title="Recommended" eyebrow="System Guidance" />
          <div className="rounded-[10px] border border-border bg-card overflow-hidden">
            {recommendations.length === 0 ? (
              <EmptyPanel title="Nothing to recommend." body="Performance signals are within expected range." />
            ) : (
              <ul className="divide-y divide-border">
                {recommendations.map((rec) => <RecommendationRow key={rec.key} rec={rec} />)}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section>
        <SectionHeader title="Revenue Quality" eyebrow={periodLabel(period)} />
        <div className="grid grid-cols-3 border-y border-border tabular-nums">
          <RevenueCell label="Won" count={summary.wonCount} amount={summary.bookedRevenue} />
          <RevenueCell label="Pending" count={Math.max(summary.wonCount - summary.netJobs, 0)} amount={summary.pendingRevenue} accent="warn" border />
          <RevenueCell label="Net" count={summary.netJobs} amount={summary.netRevenue} accent="good" border />
        </div>
        <p className="text-[12px] text-muted-foreground mt-2.5">
          Net uses finalized jobs after closeout, cancellation, and financing outcomes.
        </p>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
        <div>
          <SectionHeader title="Growth Performance" actionLabel="All Channels" onAction={() => navigate("/growth-channels")} />
          <ul className="divide-y divide-border border-y border-border">
            {channels.length === 0 ? (
              <li className="py-8 text-center text-[13px] text-muted-foreground">No channel activity in this period.</li>
            ) : channels.map((channel, index) => (
              <RankedRow
                key={channel.id}
                rank={index + 1}
                primary={channel.channelName}
                secondary={`${fmtCount(channel.leads)} Leads - ${fmtCount(channel.netJobs)} Net - ${channel.leadQuality}% Quality`}
                value={channel.cpa !== null ? fmtMoney(channel.cpa) : "-"}
                valueLabel="CPA"
                status={channel.status === "Healthy" ? "good" : channel.status === "Needs Attention" ? "warn" : "bad"}
              />
            ))}
          </ul>
        </div>

        <div>
          <SectionHeader title="Rep Performance" actionLabel="Open Rep Scorecards" onAction={() => navigate("/rep-scorecards")} />
          <ul className="divide-y divide-border border-y border-border">
            {reps.length === 0 ? (
              <li className="py-8 text-center text-[13px] text-muted-foreground">No rep outcomes in this period.</li>
            ) : reps.map((rep, index) => (
              <RankedRow
                key={rep.id}
                rank={index + 1}
                primary={rep.repName}
                secondary={rep.insight || `${rep.bookedCount} booked from ${rep.sampleSize} outcomes`}
                value={`${rep.conversionRate}%`}
                valueLabel={`${fmtCount(rep.sampleSize)} Sample`}
                status={rep.insightTone === "positive" ? "good" : rep.insightTone === "caution" ? "warn" : "neutral"}
              />
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}

function CommandDashboardOperationsServer() {
  const [activeTab, setActiveTab] = useState<"action-now" | "upcoming" | "unscheduled" | "all">("action-now");
  const [readinessFilter, setReadinessFilter] = useState<"all" | "not-ready" | "partial" | "ready">("all");
  const { summary, rows, count, loading, error, refresh } = useCommandDashboardOperationsData({
    enabled: true,
    page: 0,
    pageSize: 100,
  });

  const tabCounts = useMemo(() => {
    const actionNow = rows.filter((row) => row.needsAction).length;
    const upcoming = rows.filter((row) => row.scheduledBucket === "tomorrow" || row.scheduledBucket === "upcoming").length;
    const unscheduled = rows.filter((row) => row.scheduledBucket === "unscheduled").length;
    return { actionNow, upcoming, unscheduled, all: count };
  }, [count, rows]);

  const filteredRows = useMemo(() => {
    const byTab = rows.filter((row) => {
      if (activeTab === "action-now") return row.needsAction;
      if (activeTab === "upcoming") return row.scheduledBucket === "tomorrow" || row.scheduledBucket === "upcoming";
      if (activeTab === "unscheduled") return row.scheduledBucket === "unscheduled";
      return true;
    });
    return byTab.filter((row) => {
      if (readinessFilter === "all") return true;
      if (readinessFilter === "ready") return row.readinessStatus === "Ready for Install";
      if (readinessFilter === "partial") return row.readinessStatus === "Partially Ready";
      return row.readinessStatus === "Not Ready";
    });
  }, [activeTab, readinessFilter, rows]);

  const readinessCounts = useMemo(() => ({
    all: rows.filter((row) => row.needsAction).length,
    notReady: rows.filter((row) => row.readinessStatus === "Not Ready").length,
    partial: rows.filter((row) => row.readinessStatus === "Partially Ready").length,
    ready: rows.filter((row) => row.readinessStatus === "Ready for Install").length,
  }), [rows]);

  const lanes = useMemo(() => {
    const grouped = {
      handle: [] as CommandDashboardOperationsRow[],
      next: [] as CommandDashboardOperationsRow[],
      stuck: [] as CommandDashboardOperationsRow[],
    };
    filteredRows.forEach((row) => {
      if (
        row.priority === "critical" ||
        row.scheduledBucket === "today" ||
        (row.daysUntil !== null && row.daysUntil >= 0 && row.daysUntil <= 1 && row.needsAction)
      ) {
        grouped.handle.push(row);
      } else if (
        row.scheduledBucket === "tomorrow" ||
        row.scheduledBucket === "upcoming" ||
        (row.daysUntil !== null && row.daysUntil >= 0 && row.daysUntil <= 3)
      ) {
        grouped.next.push(row);
      } else {
        grouped.stuck.push(row);
      }
    });
    Object.values(grouped).forEach((items) => {
      items.sort((a, b) => b.revenueAtRisk - a.revenueAtRisk || a.staleDays - b.staleDays);
    });
    return grouped;
  }, [filteredRows]);

  const quickActions = [
    { label: "Order Materials", count: summary.orderMaterialsCount, icon: Package, route: "/ordering-queue" },
    { label: "Submit Permits", count: summary.submitPermitsCount, icon: Shield, route: "/permitting" },
    { label: "Assign Crew", count: summary.assignCrewCount, icon: Users, route: "/scheduling-dispatch" },
    { label: "Resolve Issues", count: summary.resolveIssuesCount, icon: Wrench, route: "/resolve?focus=issues" },
  ].filter((action) => action.count > 0);

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto w-full">
      <header className="flex items-end justify-between gap-6">
        <p className="text-[13px] text-muted-foreground tabular-nums">
          Operations lens - <span className="text-foreground font-medium">{fmtCount(summary.activeJobs)}</span> active jobs - <span className="text-foreground font-medium">{fmtCount(summary.blockedJobs)}</span> blocked
        </p>
        <button
          type="button"
          onClick={refresh}
          className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        >
          Refresh
        </button>
      </header>

      {error ? <ServerErrorNotice /> : null}
      {loading ? <LoadingLine /> : null}

      <StatStrip
        items={[
          { label: "Jobs Today", value: fmtCount(summary.jobsToday) },
          { label: "Blocked", value: fmtCount(summary.blockedJobs), accent: summary.blockedJobs > 0 ? "bad" : "good" },
          { label: "At Risk 48h", value: fmtCount(summary.atRisk48h), accent: summary.atRisk48h > 0 ? "bad" : "good" },
          { label: "Not Ready", value: fmtCount(summary.notReadyJobs), accent: summary.notReadyJobs > 0 ? "bad" : "good" },
          { label: "Open Issues", value: fmtCount(summary.openIssues), accent: summary.openIssues > 0 ? "warn" : "good" },
          { label: "$ Blocked", value: fmtMoney(summary.blockedRevenue), accent: summary.blockedRevenue > 0 ? "bad" : undefined },
          { label: "$ At Risk", value: fmtMoney(summary.atRiskRevenue), accent: summary.atRiskRevenue > 0 ? "warn" : undefined },
          { label: "$ Unsched", value: fmtMoney(summary.unscheduledRevenue) },
        ]}
      />

      {quickActions.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap border-t border-border/40 pt-3">
          <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-muted-foreground/70 mr-1">
            Quick actions
          </span>
          {quickActions.map((action) => (
            <Link
              key={action.label}
              to={action.route}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/60 bg-card text-[12px] text-foreground hover:bg-muted/40 transition-colors"
            >
              <action.icon className="h-3.5 w-3.5 text-muted-foreground" />
              {action.label}
              <span className="text-[11px] tabular-nums text-muted-foreground/80">{fmtCount(action.count)}</span>
            </Link>
          ))}
        </div>
      )}

      {summary.intakeReviewRequiredCount > 0 && (
        <Link
          to="/intake-review"
          className="flex items-center gap-3 px-3 py-2 rounded-md border border-border/60 bg-card hover:bg-muted/30 transition-colors"
        >
          <Shield className="h-3.5 w-3.5 text-[hsl(var(--warning))] shrink-0" />
          <div className="flex-1 min-w-0 flex items-baseline gap-2">
            <span className="text-[12px] font-medium text-foreground">Intake review required</span>
            <span className="text-[11px] text-muted-foreground/80">
              {fmtCount(summary.intakeReviewRequiredCount)} job{summary.intakeReviewRequiredCount === 1 ? "" : "s"} pending before entering operations
            </span>
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/70" />
        </Link>
      )}

      <section className="space-y-3">
        <div className="border-b border-border">
          <div className="flex items-center gap-5">
            <OpsTab active={activeTab === "action-now"} icon={Zap} label="Action Now" onClick={() => setActiveTab("action-now")} />
            <OpsTab active={activeTab === "upcoming"} icon={Calendar} label={`Upcoming (${fmtCount(tabCounts.upcoming)})`} onClick={() => setActiveTab("upcoming")} />
            <OpsTab active={activeTab === "unscheduled"} icon={Clock} label={`Unscheduled (${fmtCount(tabCounts.unscheduled)})`} onClick={() => setActiveTab("unscheduled")} />
            <OpsTab active={activeTab === "all"} icon={List} label={`All (${fmtCount(tabCounts.all)})`} onClick={() => setActiveTab("all")} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {([
            { key: "all", label: "All", count: readinessCounts.all },
            { key: "not-ready", label: "Not Ready", count: readinessCounts.notReady },
            { key: "partial", label: "Partial", count: readinessCounts.partial },
            { key: "ready", label: "Ready", count: readinessCounts.ready },
          ] as const).map((filter) => (
            <button
              key={filter.key}
              type="button"
              onClick={() => setReadinessFilter(filter.key)}
              className={cn(
                "text-[10px] font-medium px-2.5 py-1.5 rounded-md border transition-colors",
                readinessFilter === filter.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted/20 text-muted-foreground/60 border-border/50 hover:bg-muted/40",
              )}
            >
              {filter.label} ({fmtCount(filter.count)})
            </button>
          ))}
        </div>

        {filteredRows.length === 0 ? (
          <EmptyPanel title="All Clear" body="No jobs match this operations lens." />
        ) : (
          <div className="space-y-7 pt-2">
            <OpsLane
              icon={<XCircle className="h-4 w-4 text-destructive" />}
              title="Handle Immediately"
              tone="danger"
              rows={lanes.handle}
            />
            <OpsLane
              icon={<AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />}
              title="Next Up"
              tone="warning"
              rows={lanes.next}
            />
            <OpsLane
              icon={<Clock className="h-4 w-4 text-muted-foreground" />}
              title="Stuck / Aging"
              tone="muted"
              rows={lanes.stuck}
            />
          </div>
        )}
      </section>
    </div>
  );
}

function buildRecommendations(
  summary: CommandDashboardStrategicSummary,
  channels: CommandDashboardChannelRow[],
  reps: CommandDashboardRepRow[],
): Recommendation[] {
  const recs: Recommendation[] = [];
  const topHealthy = channels.find((c) => c.status === "Healthy" && (c.leads >= 5 || c.netJobs >= 2));
  if (topHealthy) {
    recs.push({
      key: `channel:${topHealthy.id}:increase`,
      subject: topHealthy.channelName,
      action: "Increase focus",
      outcome: `${topHealthy.cpa !== null ? fmtMoney(topHealthy.cpa) : "No CPA"} - ${topHealthy.leadQuality}% quality`,
      severity: "positive",
      route: "/growth-channels",
    });
  }

  const weakest = channels
    .filter((c) => c.leads >= 3)
    .sort((a, b) => a.leadQuality - b.leadQuality)[0];
  if (weakest && weakest.status === "Underperforming") {
    recs.push({
      key: `channel:${weakest.id}:review`,
      subject: weakest.channelName,
      action: "Review spend",
      outcome: `${weakest.leadQuality}% quality from ${fmtCount(weakest.leads)} leads`,
      severity: "caution",
      route: "/growth-channels",
    });
  }

  const topRep = reps.find((r) => r.sampleSize >= 3 && r.insightTone === "positive");
  if (topRep) {
    recs.push({
      key: `rep:${topRep.id}:route`,
      subject: topRep.repName,
      action: "Route more high-fit leads",
      outcome: `${topRep.conversionRate}% conversion`,
      severity: "positive",
      route: "/rep-scorecards",
    });
  }

  if (summary.pendingRevenue > 0) {
    recs.push({
      key: "revenue:pending:close",
      subject: "Pending revenue",
      action: "Close loop",
      outcome: `${fmtMoney(summary.pendingRevenue)} still pending`,
      severity: "caution",
      route: "/intelligence",
    });
  }

  return recs.slice(0, 4);
}

function PeriodSwitch({ value, onChange }: { value: CommandDashboardPeriod; onChange: (period: CommandDashboardPeriod) => void }) {
  const opts: { id: CommandDashboardPeriod; label: string }[] = [
    { id: "today", label: "Today" },
    { id: "week", label: "7 Days" },
    { id: "month", label: "30 Days" },
  ];
  return (
    <div className="inline-flex items-center text-[13px]" role="tablist" aria-label="Dashboard period">
      {opts.map((option, index) => (
        <button
          key={option.id}
          type="button"
          role="tab"
          aria-selected={value === option.id}
          onClick={() => onChange(option.id)}
          className={cn(
            "px-2.5 py-1 transition-colors",
            value === option.id ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground",
            index > 0 && "border-l border-border",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ActionRow({ action, primary }: { action: CommandDashboardActionRow; primary: boolean }) {
  const empty = action.itemCount === 0;
  const dot = empty
    ? "bg-[hsl(var(--success))]"
    : action.severity === "critical"
      ? "bg-destructive"
      : action.severity === "warn"
        ? "bg-[hsl(var(--warning))]"
        : "bg-muted-foreground/40";
  const phoneTail = action.topPhone ? action.topPhone.replace(/\D/g, "").slice(-4) : "";

  return (
    <li className="flex items-start gap-4 px-5 py-3.5 hover:bg-muted/40 transition-colors">
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0 mt-[7px]", dot)} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[15px] text-foreground">{action.label}</span>
          <span className="text-[13px] text-muted-foreground tabular-nums">{fmtCount(action.itemCount)}</span>
        </div>
        {empty ? (
          <div className="text-[13px] text-muted-foreground truncate">Clear</div>
        ) : (
          <div className="text-[13px] text-muted-foreground truncate mt-0.5">
            {action.topName && <span className="text-foreground font-medium">{action.topName}</span>}
            {action.topReason && (
              <>
                {action.topName && " - "}
                <span>{action.topReason}</span>
              </>
            )}
            {phoneTail && <span className="text-muted-foreground/70 tabular-nums"> - **{phoneTail}</span>}
          </div>
        )}
      </div>
      <Link
        to={action.route}
        aria-disabled={empty}
        className={cn(
          "text-[13px] px-3 py-1.5 rounded-md transition-colors shrink-0",
          empty
            ? "text-muted-foreground/50 pointer-events-none"
            : primary
              ? "bg-foreground text-background hover:bg-foreground/90 font-semibold"
              : "text-foreground hover:bg-muted",
        )}
      >
        {action.primaryCta}
      </Link>
    </li>
  );
}

function RecommendationRow({ rec }: { rec: Recommendation }) {
  const dot = rec.severity === "positive" ? "bg-[hsl(var(--success))]" : rec.severity === "caution" ? "bg-[hsl(var(--warning))]" : "bg-muted-foreground/40";
  return (
    <li className="flex items-start gap-4 px-5 py-3.5 hover:bg-muted/40 transition-colors">
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0 mt-[7px]", dot)} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] leading-snug">
          <span className="font-semibold text-foreground">{rec.subject}</span>
          <span className="text-foreground"> - {rec.action}</span>
        </div>
        <div className="text-[12px] text-muted-foreground tabular-nums truncate mt-0.5">{rec.outcome}</div>
      </div>
      <Link to={rec.route} className="text-[13px] px-3 py-1.5 rounded-md bg-foreground text-background hover:bg-foreground/90 font-semibold transition-colors shrink-0">
        Open
      </Link>
    </li>
  );
}

function RevenueCell({ label, count, amount, accent, border }: { label: string; count: number; amount: number; accent?: "good" | "warn"; border?: boolean }) {
  return (
    <div className={cn("px-5 py-4", border && "border-l border-border")}>
      <div className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-medium">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className={cn("text-[22px] font-semibold tracking-[-0.018em] leading-none", accent === "good" ? "text-[hsl(var(--success))]" : accent === "warn" ? "text-[hsl(var(--warning))]" : "text-foreground")}>{fmtCount(count)}</span>
        <span className="text-[13px] text-muted-foreground">{fmtMoney(amount)}</span>
      </div>
    </div>
  );
}

function RankedRow({ rank, primary, secondary, value, valueLabel, status }: { rank: number; primary: string; secondary: string; value: string; valueLabel: string; status: "good" | "warn" | "bad" | "neutral" }) {
  const dot = status === "good" ? "bg-[hsl(var(--success))]" : status === "warn" ? "bg-[hsl(var(--warning))]" : status === "bad" ? "bg-destructive" : "bg-muted-foreground/30";
  return (
    <li className="flex items-center gap-4 px-1 py-3.5">
      <span className="text-[12px] text-muted-foreground font-medium tabular-nums w-4 text-right">{rank}</span>
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", dot)} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="text-[15px] text-foreground truncate leading-tight">{primary}</div>
        <div className="text-[12px] text-muted-foreground truncate mt-0.5">{secondary}</div>
      </div>
      <div className="text-right tabular-nums shrink-0">
        <div className="text-[15px] font-semibold text-foreground leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-medium mt-1.5">{valueLabel}</div>
      </div>
    </li>
  );
}

function OpsTab({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 h-9 border-b-2 px-0.5 text-[12px] font-semibold transition-colors",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

function OpsLane({
  icon,
  title,
  tone,
  rows,
}: {
  icon: ReactNode;
  title: string;
  tone: "danger" | "warning" | "muted";
  rows: CommandDashboardOperationsRow[];
}) {
  if (rows.length === 0) return null;
  const total = rows.reduce((sum, row) => sum + row.revenueAtRisk, 0);
  const visible = rows.slice(0, 5);
  const toneClass = tone === "danger" ? "text-destructive" : tone === "warning" ? "text-[hsl(var(--warning))]" : "text-muted-foreground";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-0.5">
        {icon}
        <h3 className={cn("text-[13px] font-semibold tracking-tight", toneClass)}>{title}</h3>
        <span className="text-[11px] text-muted-foreground/70 tabular-nums">
          - {fmtCount(rows.length)} job{rows.length === 1 ? "" : "s"}
          {total > 0 ? ` - ${fmtMoney(total)} at risk` : ""}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visible.map((row) => <OpsCard key={row.jobId} row={row} />)}
      </div>
      {rows.length > visible.length && (
        <button
          type="button"
          className="text-[11px] text-primary hover:underline font-semibold inline-flex items-center gap-1 px-0.5"
        >
          View All ({fmtCount(rows.length)})
          <ArrowRight className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function OpsCard({ row }: { row: CommandDashboardOperationsRow }) {
  const issue = operationIssueLabel(row);
  const issueTone = row.openIssueCount > 0 || row.readinessStatus === "Not Ready" || row.isBlocked
    ? "text-destructive"
    : row.readinessStatus === "Partially Ready"
      ? "text-[hsl(var(--warning))]"
      : "text-muted-foreground";

  return (
    <div className="rounded-md border bg-card transition-colors cursor-default hover:bg-muted/20 border-border/60">
      <div className="px-3 py-2.5 space-y-1.5">
        <div className="flex items-center gap-2.5">
          <p className="text-[13px] font-semibold text-foreground leading-tight truncate">
            {[row.customerName, row.city].filter(Boolean).join(" - ")}
          </p>
          {row.customerPhone && (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/60 shrink-0 tabular-nums">
              <Phone className="h-3 w-3" />
              {row.customerPhone}
            </span>
          )}
        </div>
        <p className="text-[12px] leading-tight truncate">
          <span className="text-muted-foreground/60">{row.projectType}</span>
          {issue && (
            <>
              <span className="text-muted-foreground/60"> - </span>
              <span className={cn("font-medium", issueTone)}>{issue}</span>
            </>
          )}
        </p>
        <div className="flex items-center justify-between gap-3 pt-0.5">
          <span className="text-[11px] text-muted-foreground/60 tabular-nums truncate">
            {operationTiming(row)} - <span className="text-muted-foreground/80">{fmtMoney(row.revenueAtRisk)}</span>
          </span>
          <Link
            to={row.nextActionRoute}
            className="shrink-0 inline-flex items-center px-2.5 py-1 text-[11px] font-medium rounded-md border border-border/60 bg-card text-foreground hover:bg-muted/40 transition-colors whitespace-nowrap"
          >
            {operationActionLabel(row.nextAction)}
          </Link>
        </div>
      </div>
    </div>
  );
}

function operationIssueLabel(row: CommandDashboardOperationsRow): string {
  if (row.blockerReason) return row.blockerReason;
  if (row.openIssueCount > 0) return `${fmtCount(row.openIssueCount)} open issue${row.openIssueCount === 1 ? "" : "s"}`;
  if (row.readinessStatus === "Not Ready") return "Readiness incomplete";
  if (row.readinessStatus === "Partially Ready") return "Partially ready";
  return row.currentStage;
}

function operationTiming(row: CommandDashboardOperationsRow): string {
  if (row.scheduledBucket === "today") return "Install Today";
  if (row.scheduledBucket === "tomorrow") return "Install Tomorrow";
  if (row.daysUntil !== null && row.daysUntil >= 0) return `Installs in ${row.daysUntil}d`;
  return "Not Scheduled";
}

function operationActionLabel(action: string): string {
  if (action === "Obtain Permit") return "Submit Permit";
  if (action === "Schedule Install") return "Assign Crew";
  return action;
}

function EmptyPanel({ title, body }: { title: string; body: string }) {
  return (
    <div className="px-5 py-10 text-center">
      <p className="text-[15px] text-foreground">{title}</p>
      <p className="text-[13px] text-muted-foreground mt-1">{body}</p>
    </div>
  );
}

function LoadingLine() {
  return <div className="h-1 w-full overflow-hidden rounded-full bg-muted"><div className="h-full w-1/3 animate-pulse rounded-full bg-primary/50" /></div>;
}

function ServerErrorNotice() {
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[13px] text-destructive">
      Command dashboard server views are not available yet. Run the command-dashboard SQL migration, then refresh.
    </div>
  );
}

export default CommandDashboardServerLensBody;
